import * as THREE from 'three';
import { BUMP_K, cached, emis, flockTex, glowMat, LEATHER_BELT, std, stone } from './materials.ts';
import type { Rng } from './rand.ts';

/* Small building blocks shared by every procedural model. */

export const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const UP = V(0, 1, 0);

/** Creates a mesh and optionally parents it. */
export const M = (geo: THREE.BufferGeometry, mat: THREE.Material, parent?: THREE.Object3D): THREE.Mesh => {
  const m = new THREE.Mesh(geo, mat);
  if (parent) parent.add(m);
  return m;
};

/** Group helper: new group at (x,y,z) under parent. */
export const G = (parent: THREE.Object3D, x = 0, y = 0, z = 0): THREE.Group => {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  parent.add(g);
  return g;
};

/** Tapered cylinder from a (radius r1) to b (radius r2). */
export const limb = (
  parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number, mat: THREE.Material, seg = 8,
): THREE.Mesh => {
  const v = new THREE.Vector3().subVectors(b, a);
  const len = v.length();
  const m = M(new THREE.CylinderGeometry(r2, r1, len, seg), mat, parent);
  m.position.copy(a).addScaledVector(v, 0.5);
  m.quaternion.setFromUnitVectors(UP, v.normalize());
  return m;
};

/** Additive glow billboard. Pass `unique` when the caller animates its opacity. */
export const glowSprite = (
  parent: THREE.Object3D, color: string, size: number, pos?: THREE.Vector3, op = 0.8, unique = false,
): THREE.Sprite => {
  const s = new THREE.Sprite(glowMat(color, op, unique));
  s.scale.setScalar(size);
  if (pos) s.position.copy(pos);
  parent.add(s);
  return s;
};

export interface Flame {
  group: THREE.Group;
  flick(): void;
}

/** Two flickering cones plus a glow sprite. */
export const flame = (parent: THREE.Object3D, pos: THREE.Vector3, s: number, rng: Rng, color = '#ff7a1a'): Flame => {
  const g = G(parent, pos.x, pos.y, pos.z);
  const fm = emis('#ffb347', color, 1.2, { transparent: true, opacity: 0.9, depthWrite: false });
  const c1 = M(new THREE.ConeGeometry(0.08 * s, 0.3 * s, 7), fm, g);
  c1.position.y = 0.12 * s;
  const c2 = M(new THREE.ConeGeometry(0.05 * s, 0.2 * s, 6), fm, g);
  c2.position.set(0.04 * s, 0.1 * s, 0.02 * s);
  glowSprite(g, color === '#ff7a1a' ? '#ff8a2a' : color, 0.8 * s, V(0, 0.12 * s, 0), 0.7);
  const R = (): number => rng() - 0.5;
  return {
    group: g,
    flick: () => {
      c1.scale.set(1 + R() * 0.3, 1 + R() * 0.6, 1 + R() * 0.3);
      c2.scale.set(1 + R() * 0.3, 1 + R() * 0.7, 1);
    },
  };
};

/** A "boiling" tube: its control points are re-jittered every step (stop-motion smoke, cracks, hair). */
export class Ribbon {
  readonly mesh: THREE.Mesh;
  constructor(
    parent: THREE.Object3D,
    private readonly pts: THREE.Vector3[],
    private readonly rad: number,
    mat: THREE.Material,
    private readonly rng: Rng,
    private readonly jit = 0.03,
  ) {
    this.mesh = M(this.geo(), mat, parent);
  }
  private geo(): THREE.TubeGeometry {
    const j = this.jit * 2;
    const R = (): number => (this.rng() - 0.5) * j;
    const p = this.pts.map((v) => v.clone().add(V(R(), R(), R())));
    return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(p), Math.max(8, p.length * 2), this.rad, 5, false);
  }
  step(): void {
    this.mesh.geometry.dispose();
    this.mesh.geometry = this.geo();
  }
}

/** Points of a vertical wavy wisp. */
export const wisp = (x: number, z: number, h: number, n: number): THREE.Vector3[] => {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) pts.push(V(x + Math.sin(i * 0.9) * 0.08, (i / (n - 1)) * h, z + Math.cos(i * 0.7) * 0.08));
  return pts;
};

/** Heater (kite-ish) shield, face toward +Z, centered on its own origin. */
export const heaterShield = (w: number, h: number, mat: THREE.Material): THREE.Mesh => {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, h / 2);
  s.lineTo(w / 2, h / 2);
  s.lineTo(w / 2, 0);
  s.quadraticCurveTo(w / 2, -h * 0.35, 0, -h / 2);
  s.quadraticCurveTo(-w / 2, -h * 0.35, -w / 2, 0);
  s.closePath();
  return M(new THREE.ExtrudeGeometry(s, { depth: 0.035, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.012, bevelSegments: 1, curveSegments: 6 }), mat);
};

/** Longsword pointing +Y, grip at the origin. */
export const sword = (len: number, bladeMat: THREE.Material, hiltMat: THREE.Material): THREE.Group => {
  const g = new THREE.Group();
  M(new THREE.BoxGeometry(0.045, len, 0.012), bladeMat, g).position.y = len / 2 + 0.12;
  const tip = M(new THREE.ConeGeometry(0.032, 0.08, 4), bladeMat, g);
  tip.position.y = len + 0.16;
  tip.rotation.y = Math.PI / 4;
  tip.scale.z = 0.3;
  M(new THREE.BoxGeometry(0.2, 0.03, 0.04), hiltMat, g).position.y = 0.11;
  M(new THREE.CylinderGeometry(0.018, 0.018, 0.13, 6), LEATHER_BELT, g).position.y = 0.04;
  M(new THREE.SphereGeometry(0.03, 6, 5), hiltMat, g).position.y = -0.03;
  return g;
};

/** Black display base with static-grass flock and a few pebbles (top surface at y = 0). */
export const peana = (r: number, rng: Rng): THREE.Group => {
  const g = new THREE.Group();
  g.name = 'peana';
  const side = M(new THREE.CylinderGeometry(r, r * 1.04, 0.12, 32), std('#111111', { roughness: 0.35 }), g);
  side.position.y = -0.06;
  const topMat = cached('peanaTop', () => new THREE.MeshStandardMaterial({
    color: '#ffffff', map: flockTex(), roughness: 1, bumpMap: flockTex(), bumpScale: 0.03 * BUMP_K,
  }));
  const top = M(new THREE.CircleGeometry(r * 0.97, 32), topMat, g);
  top.rotation.x = -Math.PI / 2;
  top.position.y = 0.002;
  const pm = stone('#6d6559');
  for (let i = 0; i < 5; i++) {
    const st = M(new THREE.DodecahedronGeometry(0.03 + rng() * 0.04), pm, g);
    const a = rng() * 6.3, rr = r * (0.3 + rng() * 0.6);
    st.position.set(Math.cos(a) * rr, 0.02, Math.sin(a) * rr);
  }
  return g;
};

/** Two glowing eyes facing +Z. */
export const eyesGlow = (parent: THREE.Object3D, color: string, y: number, z: number, gap: number, size = 0.022): void => {
  const em = emis('#ffffff', color, 1.4);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(size, 6, 5), em, parent).position.set(k * gap, y, z);
    glowSprite(parent, color, size * 7.3, V(k * gap, y, z + 0.01), 0.9);
  }
};

/** Torus lying flat (axis = Y). */
export const ring = (
  parent: THREE.Object3D, r: number, tube: number, mat: THREE.Material, y = 0, radial = 6, tubular = 24,
): THREE.Mesh => {
  const m = M(new THREE.TorusGeometry(r, tube, radial, tubular), mat, parent);
  m.rotation.x = Math.PI / 2;
  m.position.y = y;
  return m;
};
